import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccessTime as AccessTimeIcon,
  LocalHospital as HospitalIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import axiosInstance from '../../config/axios';

const EstadisticasAdmin = () => {
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEstadisticas();
  }, []);

  const fetchEstadisticas = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/admin/estadisticas-detalladas');
      setEstadisticas(response.data);
    } catch (error) {
      console.error('Error al obtener estadísticas:', error);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        {error}
      </Alert>
    );
  }

  const cards = [
    {
      title: 'Casos Totales',
      value: estadisticas?.casosTotales || 0,
      icon: <AssignmentIcon sx={{ fontSize: 40 }} />,
      color: '#2196f3',
      secondary: 'Todos los casos registrados'
    },
    {
      title: 'Casos Urgentes',
      value: estadisticas?.casosUrgentes || 0,
      icon: <WarningIcon sx={{ fontSize: 40 }} />,
      color: '#f44336',
      secondary: 'Casos clasificados como ESI-1 o ESI-2'
    },
    {
      title: 'Casos Completados',
      value: estadisticas?.casosCompletados || 0,
      icon: <CheckIcon sx={{ fontSize: 40 }} />,
      color: '#4caf50',
      secondary: 'Casos finalizados exitosamente'
    },
    {
      title: 'Tiempo Promedio',
      value: `${estadisticas?.tiempoPromedioMinutos || 0} min`,
      icon: <AccessTimeIcon sx={{ fontSize: 40 }} />,
      color: '#ff9800',
      secondary: 'Tiempo promedio de atención'
    }
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Estadísticas del Sistema
      </Typography>

      {/* Tarjetas principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                transition: 'transform 0.3s, box-shadow 0.3s',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: `${card.color}15`,
                      borderRadius: '50%',
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {React.cloneElement(card.icon, { sx: { ...card.icon.props.sx, color: card.color } })}
                  </Box>
                  <Typography variant="h4" component="div" gutterBottom>
                    {card.value}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary">
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {card.secondary}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Estadísticas detalladas */}
      <Grid container spacing={3}>
        {/* Distribución por ESI */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Distribución por Nivel ESI
            </Typography>
            <List>
              {estadisticas?.distribucionESI?.map((item, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <TimelineIcon sx={{ color: item.color || '#666' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`ESI-${item.nivel}`}
                    secondary={`${item.cantidad} casos (${item.porcentaje}%)`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Estadísticas de usuarios */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Estadísticas de Usuarios
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Total de Usuarios"
                  secondary={estadisticas?.totalUsuarios || 0}
                />
              </ListItem>
              <Divider sx={{ my: 1 }} />
              {estadisticas?.usuariosPorRol?.map((rol, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <HospitalIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={rol._id}
                    secondary={`${rol.cantidad} usuarios`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Tendencias */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tendencias y Métricas
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUpIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Casos esta semana"
                      secondary={estadisticas?.casosSemana || 0}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <AccessTimeIcon color="secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Tiempo promedio de respuesta"
                      secondary={`${estadisticas?.tiempoPromedioRespuesta || 0} minutos`}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Tasa de éxito"
                      secondary={`${estadisticas?.tasaExito || 0}%`}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EstadisticasAdmin; 